"use client"

import { cn } from "@/lib/utils"

interface Opt { id: string; label: string; hint?: string }

export function SingleChoice({ name, options, value, onChange }: {
  name: string; options: readonly Opt[]; value: string | null; onChange: (v: string) => void
}) {
  return (
    <div role="radiogroup" className="grid gap-2">
      {options.map((o) => (
        <label key={o.id} className={cn(
          "flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-base transition-colors",
          value === o.id ? "border-accent bg-accent/10" : "border-border hover:bg-card",
        )}>
          <input type="radio" name={name} className="mt-1 h-4 w-4" checked={value === o.id} onChange={() => onChange(o.id)} />
          <span>{o.label}{o.hint ? <span className="ml-2 text-xs text-muted-foreground">({o.hint})</span> : null}</span>
        </label>
      ))}
    </div>
  )
}

export function MultiChoice({ options, values, onChange, exclusive }: {
  options: readonly Opt[]; values: string[]; onChange: (v: string[]) => void
  /** Option ids that clear all others when picked (e.g. "none of these"). */
  exclusive?: string[]
}) {
  const toggle = (id: string) => {
    if (values.includes(id)) return onChange(values.filter((v) => v !== id))
    if (exclusive?.includes(id)) return onChange([id])
    onChange([...values.filter((v) => !exclusive?.includes(v)), id])
  }
  return (
    <div className="grid gap-2">
      {options.map((o) => (
        <label key={o.id} className={cn(
          "flex cursor-pointer items-start gap-3 rounded-lg border p-4 text-base transition-colors",
          values.includes(o.id) ? "border-accent bg-accent/10" : "border-border hover:bg-card",
        )}>
          <input type="checkbox" className="mt-1 h-4 w-4" checked={values.includes(o.id)} onChange={() => toggle(o.id)} />
          <span>{o.label}{o.hint ? <span className="ml-2 text-xs text-muted-foreground">({o.hint})</span> : null}</span>
        </label>
      ))}
    </div>
  )
}

export const YNU = [
  { id: "yes", label: "Yes" },
  { id: "no", label: "No" },
  { id: "unknown", label: "I don't know" },
] as const
